import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  People, Assignment, CalendarToday, Settings, Dashboard as DashboardIcon,
  Edit, EmojiEvents, CheckCircle, Assessment, EventNote,
  Event, EventAvailable, Person, AttachMoney, Paid,
  ManageAccounts, FolderOpen, HowToReg, VerifiedUser, School,
  VideoCall, AddCircle
} from '@mui/icons-material';
import AppShell, { NavItem, NavSubItem, NavGroup, NavDivider } from '../components/layout/AppShell';
import InternshipManagement from '../components/InternshipManagement';
import { useAuth } from '../contexts/AuthContext';
import TeamDashboard from '../components/TeamDashboard';
import MyTimesheets from '../components/MyTimesheets';
import TimesheetEntry from '../components/TimesheetEntry';
import Reports from '../components/Reports';
import ChangePassword from '../components/ChangePassword';
import AttendanceTracking from '../components/AttendanceTracking';
import AdminAttendanceLeave from '../components/AdminAttendanceLeave';
import LeaveManagement from '../components/LeaveManagement';
import HolidayList from '../components/HolidayList';
import GoalManagement from '../components/GoalManagement';
import MyEmployeeDetails from '../components/MyEmployeeDetails';
import SalaryReport from '../components/SalaryReport';
import OnboardingReview from '../components/OnboardingReview';
import EmployeeDetails from '../components/EmployeeDetails';
import EmployeeLeaveBalance from '../components/EmployeeLeaveBalance';
import TimesheetApproval from '../components/TimesheetApproval';
import ProjectManagement from '../components/ProjectManagement';
import InterviewScheduling from '../components/InterviewScheduling';
import MyInterviewPanel from '../components/MyInterviewPanel';
import GenerateMeetingPage from '../components/GenerateMeetingPage';

interface ManagerDashboardProps {
  mobileOpen?: boolean;
  onDrawerClose?: () => void;
  onDrawerToggle?: () => void;
}

const TIMESHEET_VIEWS = ['mytimesheets', 'entry', 'approval', 'reports'];
const LEAVE_VIEWS = ['myattendance', 'allattendance', 'leave', 'leavebalance'];
const HR_VIEWS = ['empdetails', 'onboarding'];
const PAYROLL_VIEWS = ['salary'];
const MEETING_VIEWS = ['generatemeeting', 'mypanel'];

const ManagerDashboard: React.FC<ManagerDashboardProps> = ({ mobileOpen = false, onDrawerClose, onDrawerToggle }) => {
  const { can } = useAuth();
  const navigate = useNavigate();
  const { view } = useParams<{ view?: string }>();
  const currentView = view || 'team';
  // Groups start open when the URL (on first load or refresh) points at one of their
  // views, so the sidebar reveals where you are instead of always resetting to defaults.
  const [openTimesheet, setOpenTimesheet] = useState(TIMESHEET_VIEWS.includes(currentView));
  const [openLeave, setOpenLeave] = useState(LEAVE_VIEWS.includes(currentView));
  const [openHR, setOpenHR] = useState(HR_VIEWS.includes(currentView));
  const [openPayroll, setOpenPayroll] = useState(PAYROLL_VIEWS.includes(currentView));
  const [openMeetings, setOpenMeetings] = useState(MEETING_VIEWS.includes(currentView));

  // Selecting a nav item navigates to its own URL (so refresh/back-forward keep the
  // right page+sidebar state) and closes the mobile drawer — on desktop (permanent
  // drawer) onDrawerClose is a no-op since mobileOpen never becomes true there.
  const selectView = (view: string) => {
    navigate(`/dashboard/${view}`);
    onDrawerClose?.();
  };

  const renderContent = () => {
    switch (currentView) {
      case 'team':           return <TeamDashboard />;
      case 'mytimesheets':   return <MyTimesheets />;
      case 'entry':          return <TimesheetEntry />;
      case 'approval':       return <TimesheetApproval />;
      case 'reports':        return <Reports />;
      case 'goals':          return <GoalManagement />;
      case 'internships':    return <InternshipManagement />;
      case 'myattendance':   return <AttendanceTracking />;
      case 'allattendance':  return <AdminAttendanceLeave />;
      case 'leave':          return <LeaveManagement />;
      case 'leavebalance':   return <EmployeeLeaveBalance />;
      case 'holidays':       return <HolidayList />;
      case 'salary':         return <SalaryReport />;
      case 'onboarding':     return <OnboardingReview />;
      case 'empdetails':     return <EmployeeDetails />;
      case 'projects':       return <ProjectManagement />;
      case 'mydetails':      return <MyEmployeeDetails />;
      case 'interviewscheduling': return <InterviewScheduling />;
      case 'generatemeeting': return <GenerateMeetingPage />;
      case 'mypanel':        return <MyInterviewPanel />;
      case 'settings':       return <ChangePassword />;
      default:               return <TeamDashboard />;
    }
  };

  // Derived section visibility — if `can` returns true for ANY item in a section,
  // show the section header too.
  const showAttendanceSection = can('ViewTeamAttendance') || can('ViewAllAttendance') || can('ManageAttendance') || can('ApproveLeave') || can('ManageLeaveBalance');
  const showHRSection = can('ManageEmployeeDetails') || can('VerifyDocuments') || can('ReviewOnboarding') || can('ManageOnboarding');
  const showPayrollSection = can('ViewTeamPayroll') || can('ViewAllPayroll') || can('GeneratePayroll');
  const showProjectsSection = can('ViewProjects') || can('ManageProjects') || can('AssignProjects');

  const drawerContent = (
        <>
            {/* Team overview — always visible for managers */}
            <NavItem icon={<People />} label="Team Overview" active={currentView === 'team'} onClick={() => selectView('team')} />

            {/* Timesheet */}
            <NavGroup icon={<Assignment />} label="Timesheet" open={openTimesheet} onToggle={() => setOpenTimesheet(!openTimesheet)}>
                {/* Own timesheet — always available */}
                <NavSubItem icon={<CheckCircle />} label="My Timesheets" active={currentView === 'mytimesheets'} onClick={() => selectView('mytimesheets')} />
                <NavSubItem icon={<Edit />} label="Log Time" active={currentView === 'entry'} onClick={() => selectView('entry')} />
                {can('ApproveTimesheet') && (
                  <NavSubItem icon={<HowToReg />} label="Approvals" active={currentView === 'approval'} onClick={() => selectView('approval')} />
                )}
                {(can('ViewTeamReports') || can('ViewAllReports') || can('ExportReports')) && (
                  <NavSubItem icon={<Assessment />} label="Reports" active={currentView === 'reports'} onClick={() => selectView('reports')} />
                )}
              </NavGroup>

            {/* Goals */}
            {(can('ViewTeamGoals') || can('ViewAllGoals') || can('CreateGoals')) && (
              <NavItem icon={<EmojiEvents />} label="Goals" active={currentView === 'goals'} onClick={() => selectView('goals')} />
            )}

            {/* Interns this manager mentors or who report to them — filtered server-side, so
                not permission-gated (an empty list just means no interns). */}
            <NavItem icon={<School />} label="Interns" active={currentView === 'internships'} onClick={() => selectView('internships')} />

            {/* Attendance & Leave */}
            {showAttendanceSection && (
              <>
                <NavGroup icon={<CalendarToday />} label="Attendance & Leave" open={openLeave} onToggle={() => setOpenLeave(!openLeave)}>
                    <NavSubItem icon={<EventNote />} label="My Attendance" active={currentView === 'myattendance'} onClick={() => selectView('myattendance')} />
                    {(can('ViewTeamAttendance') || can('ViewAllAttendance') || can('ManageAttendance')) && (
                      <NavSubItem icon={<DashboardIcon />} label="Team Attendance" active={currentView === 'allattendance'} onClick={() => selectView('allattendance')} />
                    )}
                    {can('ApproveLeave') && (
                      <NavSubItem icon={<Event />} label="Leave Approvals" active={currentView === 'leave'} onClick={() => selectView('leave')} />
                    )}
                    {can('ManageLeaveBalance') && (
                      <NavSubItem icon={<EventAvailable />} label="Leave Balances" active={currentView === 'leavebalance'} onClick={() => selectView('leavebalance')} />
                    )}
                  </NavGroup>
              </>
            )}

            {/* Payroll */}
            {showPayrollSection && (
              <>
                <NavGroup icon={<AttachMoney />} label="Payroll" open={openPayroll} onToggle={() => setOpenPayroll(!openPayroll)}>
                    <NavSubItem icon={<Paid />} label="Salary Report" active={currentView === 'salary'} onClick={() => selectView('salary')} />
                  </NavGroup>
              </>
            )}

            {/* HR */}
            {showHRSection && (
              <>
                <NavGroup icon={<ManageAccounts />} label="HR" open={openHR} onToggle={() => setOpenHR(!openHR)}>
                    {(can('ManageEmployeeDetails') || can('VerifyDocuments')) && (
                      <NavSubItem icon={<VerifiedUser />} label="Employee Details" active={currentView === 'empdetails'} onClick={() => selectView('empdetails')} />
                    )}
                    {(can('ReviewOnboarding') || can('ManageOnboarding')) && (
                      <NavSubItem icon={<Person />} label="Onboarding Review" active={currentView === 'onboarding'} onClick={() => selectView('onboarding')} />
                    )}
                  </NavGroup>
              </>
            )}

            {/* Projects */}
            {showProjectsSection && (
              <NavItem icon={<FolderOpen />} label="Projects" active={currentView === 'projects'} onClick={() => selectView('projects')} />
            )}

            {/* Recruitment — scheduling is Admin/Manager (matches backend role gate). */}
            <NavItem icon={<EventNote />} label="Interview Scheduling" active={currentView === 'interviewscheduling'} onClick={() => selectView('interviewscheduling')} />

            {/* Meetings — Generate Meeting bundles Virtual Office presence, starting/joining a
                temporary meeting, and the join history on one page; the interview panel sits
                alongside it (a personal view, visible to any manager who's been added to a
                panel, so not gated behind a permission). Moved off the Attendance page so that
                page is attendance-only. */}
            <NavGroup icon={<VideoCall />} label="Meetings" open={openMeetings} onToggle={() => setOpenMeetings(!openMeetings)}>
                <NavSubItem icon={<AddCircle />} label="Generate Meeting" active={currentView === 'generatemeeting'} onClick={() => selectView('generatemeeting')} />
                <NavSubItem icon={<HowToReg />} label="My Interview Panel" active={currentView === 'mypanel'} onClick={() => selectView('mypanel')} />
              </NavGroup>

            <NavDivider />
            <NavItem icon={<Person />} label="My Details" active={currentView === 'mydetails'} onClick={() => selectView('mydetails')} />
            <NavItem icon={<Settings />} label="Settings" active={currentView === 'settings'} onClick={() => selectView('settings')} />
          </>
      );

  return (
    <AppShell
      panelTitle="Manager Panel"
      nav={drawerContent}
      mobileOpen={mobileOpen}
      onDrawerClose={onDrawerClose}
      onDrawerToggle={onDrawerToggle}
    >
      {renderContent()}
    </AppShell>
  );
};

export default ManagerDashboard;
